import React, {useState, useEffect} from 'react';
import { addFilter } from '@wordpress/hooks';

import SingleLicense from '../components/SingleLicense';
import CopyButton from './CopyButton';
import Pagination from '../../common/Pagination';

addFilter(
    "easycommerce_dashboard_sidebar_menu_list",
    "easycommerce-license/extend-sidebar",
    (menuList) => {
        const updatedMenuList = menuList.map(item => {
            if (item.id === "orders" && Array.isArray(item.subMenuList)) {
                return {
                    ...item,
                    subMenu: true,
                    subMenuList: [
                        ...item.subMenuList,
                        { id: "licenses", label: "Licenses" },
                    ]
                };
            }
            return item;
        });
        return updatedMenuList;
    }
);

addFilter(
    "easycommerce_dashboard_tab_content_list",
    "easycommerce-license/license-tab-content",
    (tabContentList) => {
        return [
            ...tabContentList,
            { id: "licenses", component: <DataTable /> },
        ];
    }
);

addFilter(
    "easycommerce_dashboard_valid_tabs",
    "easycommerce-license/valid-tabs",
    (validTabs) => {
        if (!validTabs.includes("licenses")) {
            validTabs.push("licenses");
        }
        return validTabs;
    }
);

addFilter(
    "easycommerce_dashboard_single_views",
    "easycommerce-license/single-license-page",
    (views) => {
        return [
            ...views,
            {
                match: /^licenses\/(\d+)$/,
                component: (id) => <SingleLicense licenseId={id} />,
            },
        ];
    }
);

const getPageFromPath = (path) => {
    const pageRegex = /\/page\/(\d+)$/;
    const match = path.match(pageRegex);
    return match ? Number(match[1]) : 1;
};

const DataTable = () => {
    const [licenseData, setLicenseData] = useState([]);
    const [totalPage, setTotalPage] = useState(1);
    const [page, setPage] = useState(1);
    const [postPerPage, setPostPerPage] = useState(10);

    const extractPageFromHash = () => {
        const hash = window.location.hash.replace("#", "");
        const [, pagePath] = hash.split("/page/");
        const currentPage = getPageFromPath(hash);
        return currentPage;
    };

    useEffect(() => {
        const handleHashChange = () => {
            const currentPage = extractPageFromHash();
            setPage(currentPage);
        };

        window.addEventListener("hashchange", handleHashChange);
        handleHashChange();

        return () => window.removeEventListener("hashchange", handleHashChange);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(
                    `${EASYCOMMERCE.rest_base}/licenses?page=${page}&per_page=${postPerPage}&customer_id=${EASYCOMMERCE.user.ID}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'X-WP-Nonce': EASYCOMMERCE.nonce,
                        },
                    }
                );
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const result = await response.json();

                setLicenseData(result.data.licenses || []);
                setTotalPage(result.data.total_pages || 1);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [page, postPerPage]);

    const formatDate = (dateString) => {
        const formattedDate = dateString.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})$/)

        return (
            <>
                <span className='block'>{formattedDate ? formattedDate[1] : 'N/A'}</span>
                <span className='block text-ec-wizard'>{formattedDate ? formattedDate[2] : 'N/A'}</span>
            </>
        )
    };

	return (
        <>
            <h3 className='easycommerce-dashboard-section-title !text-lg sm:!text-2xl'>Licenses</h3>

            <div className="w-full overflow-x-auto">
                <table className="w-full border-none m-0">
                    <thead className="easycommerce-dash-roth">
                        <tr className="h-[42px]">
                            <th className="w-[10%] font-inter font-medium text-left text-base leading-[26px] text-ec-placeholder border-b border-b-ec-border border-r-0 pl-0">
                                ID
                            </th>
                            <th className="font-inter font-medium text-left text-base leading-[26px] text-ec-placeholder border-b border-b-ec-border border-r-0">
                                Product
                            </th>
                            <th className="font-inter font-medium text-left text-base leading-[26px] text-ec-placeholder border-b border-b-ec-border border-r-0">
                                License Key
                            </th>
                            <th className="w-[15%] font-inter font-medium text-left text-base leading-[26px] text-ec-placeholder border-b border-b-ec-border border-r-0">
                                Status
                            </th>
                            <th className="font-inter font-medium text-left text-base leading-[26px] text-ec-placeholder border-b border-b-ec-border border-r-0">
                                Activation
                            </th>
                            <th className="w-[15%] font-inter font-medium text-left text-base leading-[26px] text-ec-placeholder border-b border-b-ec-border border-r-0">
                                Expiry
                            </th>
                        </tr>
                    </thead>
                    <tbody className="easycommerce-dash-roth">
                        {licenseData.length > 0 ? licenseData.map((data, index) => (
                            <tr className="h-[76px] ">
                                {/* ID */}
                                <td className="w-[10%] text-sm font-inter font-normal text-left leading-[26px] text-ec-body border-b border-b-ec-border border-r-0 pl-0">
                                    {data.id ? (
                                        <a
                                            className="text-ec-body hover:text-ec-primary duration-300"
                                            href={`#licenses/${data.id}`}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            #{data.id}
                                        </a>
                                    ) : (
                                        <span className="text-ec-light-black">N/A</span>
                                    )}
                                </td>

                                {/* Product Title */}
                                <td className="font-inter w-[25%] font-normal text-left text-sm leading-[26px] text-ec-body border-b border-b-ec-border border-r-0">
                                    <span className="block">{data.product_name}</span>
                                    <span className="text-[13px] leading-6 block text-ec-wizard">{data.product_variation}</span>
                                </td>

                                {/* License Key */}
                                <td className="font-inter font-normal text-left text-sm leading-[26px] text-ec-body border-b border-b-ec-border border-r-0">
                                    <div className="flex items-center gap-1">
                                        <span className="text-sm text-ec-body font-inter font-normal">
                                            {data.license_key.length > 10
                                                ? `${data.license_key.substring(0, 10)}...`
                                                : data.license_key}
                                        </span>
                                        <CopyButton copy={data.license_key} />
                                    </div>
                                </td>

                                {/* Status */}
                                <td className="w-[15%] font-inter font-normal text-left text-sm leading-[26px] text-ec-body border-b border-b-ec-border border-r-0">
                                    {(() => {
                                        switch (data.status) {
                                            case 'active':
                                                return <span style={{ color: '#00A900' }}>Active</span>;
                                            case 'expired':
                                                return <span className="text-ec-body">Expired</span>;
                                            case 'blocked':
                                                return <span className="text-[#FF3A52]">Blocked</span>;
                                            case 'inactive':
                                                return <span className="text-ec-body">Inactive</span>;
                                            default:
                                                return <span className="text-ec-body">Inactive</span>;
                                        }
                                    })()}
                                </td>

                                {/* Activation */}
                                <td className="w-[10%] font-inter font-normal text-left text-sm leading-[26px] text-ec-body border-b border-b-ec-border border-r-0">
                                    {data.activations}/{data.activation_limit}
                                </td>
                                
                                {/* Expiry */}
                                <td className="w-[15%] font-inter font-normal text-left text-sm leading-[26px] text-ec-body border-b border-b-ec-border border-r-0">
                                    {data.is_lifetime == true ? 'Lifetime' : formatDate(data.expiry)}
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="6" className="text-ec-body py-4 border-0" style={{ paddingLeft: '0px' }}>
                                    No licenses found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {totalPage > 1 && (
                <Pagination
                    baseSlug="licenses"
                    current={page}
                    total={totalPage}
                />
            )}
        </>
	);
};

export default DataTable;
